import { fn, INamed, htmlEncode } from '../utils';
import resolveToError = fn.resolveToError;
import ToDictionary = fn.ToDictionary;
import FindFirst = fn.FindFirst;
import { RSAKeyPair } from '../util/encryption';
import { template as ktemplate, NumericFormat, DateFormat, constants, DateTimeGroupingAdjusted } from '../navkendo/kutils';
import { Observable, ObservableArray } from "../nav/navmodel";
import {
    getClientKeyFunc, getOrCreateKendoModel, $PageItemExecutedUpdate, $PageItemExecuteUpate,
    Model, __ValidateProps, NavPageClientInfo, GridColumn
} from './navmodel';
import {
    IPageMetaData, IPageObj, IPageProperty, NavGridVisibility, TDefaultPageObj,
    IPageQueryConstraint,
    IViewPageDisplay,
    ILookupInfo,
    TDefinedPageName,
    TDefinedPageMaps,
    IDetermineDynamicLookup,
    IDetermineDynamicLookupEvent
} from '../nav/navtypes';
import { CompatibilityService, IRequestShortcutConfig } from "../services/compatibilityservice";
import { NavUrlService, TNavUrlAccess, INavUrlInfoEx } from "../services/navurlservice";
import { IDialogService } from '../services/DialogService';
import { INavPageClientInfo } from './navmodel';
import {
    INavPageClientInfo_Data, INavItemKey, INavKey, IPageQueryParameters,
    INavMethodConfig, IDMSUILine, IPageQueryResult,
} from './navdata';
import { NavPropertyItem } from './navpropertyitem';
import { NavPageItem } from './navpageitem';
import { NavItemAnchor } from './navitemanchor';
import { NavPageItems } from './navpageitems';
import { IsNavPageItem, IsNavPageItems, NavData } from './indirection';
import { InfDatePicker } from '../navkendo/infdatepicker';

/** Allows Acess to published NAV Soap Page Information (description and runtime) [BASE] */
export class NavClientService extends Observable {
    constructor(
        public readonly $c: CompatibilityService,
        public readonly NavUrl: NavUrlService,
        public readonly $navui: IDialogService
    ) {
        super();
    }

    /** Resolve UrlInfo to NavUrl */
    public ResolveUrl(urlInfo?: TNavUrlAccess, validateCompany?: boolean) {
        return this.NavUrl.ResolveUrl(urlInfo, validateCompany);
    }

    /** Get Company Names for NavUrl */
    public GetCompanies(url: TNavUrlAccess, forceRefresh?: boolean) {
        return this.ResolveUrl(url, false).then(navUrlInfo => {
            const key = navUrlInfo.BaseUrl + (navUrlInfo.QueryString || "");
            var result = (!forceRefresh) && this._getCompanyNames[key];
            if (!result)
                result = this._getCompanyNames[key] = this.$c.get<string[]>("/infoma.Web.Nav/NavPageClient/GetCompanyNames?" +
                    fn.EncodeQueryData({ navUrl: navUrlInfo.FullUrl })).then(d => d.data);
            return result;
        });
    }
    private _getCompanyNames: Record<string, PromiseLike<string[]>> = {};

    /** Get Configured NAV Accesses from Server Config */
    public GetConfigStructure(forceRefresh?: boolean) {
        var result = (!forceRefresh) && this._getConfigStructure;
        if (!result)
            result = this._getConfigStructure = this.$c.get<Array<{ Name: string, Description: string, Urls: INavUrlInfoEx[] }>>("/infoma.Web.Nav/GetUrlStructure").then(data => data.data);
        return result;
    }
    private _getConfigStructure: PromiseLike<Array<{ Name: string, Description: string, Urls: INavUrlInfoEx[] }>>;

    /** Get (Cached) Page Client Information */
    public GetPageClientInfo<TPageName extends TDefinedPageName>(pageName: TPageName, navUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INavPageClientInfo<TDefinedPageMaps[TPageName]>>;
    public GetPageClientInfo<TPage extends IPageObj = TDefaultPageObj>(pageName: string, navUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INavPageClientInfo<TPage>>;
    public GetPageClientInfo<TPage extends IPageObj>(pageName: string, navUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INavPageClientInfo<TPage>> {
        return this.GetPageClientInfos([<TDefinedPageName>pageName], navUrl, forceStatic).then(item => item[pageName]);
    }

    /** Get Multiple (Cached) Page Client Informations */
    public GetPageClientInfos<TPageName extends TDefinedPageName>(pageNames: TPageName[], fullNavUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INamed<INavPageClientInfo>>;
    //public GetPageClientInfos<TPage>(pageNames: string[], fullNavUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INamed<INavPageClientInfo<TPage>>>;
    public GetPageClientInfos<TPage>(pageNames: string[], fullNavUrl?: TNavUrlAccess, forceStatic?: boolean): PromiseLike<INamed<INavPageClientInfo<TPage>>> {
        return this.ResolveUrl(fullNavUrl).then(navUrlInfo => {
            // Normalize NavUrl (strip explicit page infos, ...)
            let navUrl = navUrlInfo.FullUrl;
            var loadingClientInfo = this.loadingClientInfo,
                $c = this.$c,
                // Key is (Pagename + navUrl + isStatic)
                getKey = (pageName: string) => [pageName, navUrl, forceStatic ? "s" : ""].join("-"),
                allInfos = pageNames.map(pageName => { return { PageName: pageName, Key: getKey(pageName) }; }),
                missingInfos = allInfos.filter(info => !loadingClientInfo[info.Key]),
                // setup final result after all load promises available
                whenAllLoaded = () => {
                    var loadClientPromises = allInfos.map(info => loadingClientInfo[info.Key]);
                    return $c.all(loadClientPromises)
                        .then(items => ToDictionary(items, info => info.MetaData.PageName));
                };

            if (missingInfos.length > 0) {
                var missingPageNames = missingInfos.map(info => info.PageName);
                var load = this.loadPageClientInfos(missingPageNames, navUrl, forceStatic).then(l => l, err => {
                    throw new Error("Das Laden der Page-Beschreibung(en) für '" + missingPageNames.join(", ") + "' ist fehlgeschlagen:" + resolveToError(err));
                });

                // Store missing load promises
                const perPage = missingInfos.map(m => loadingClientInfo[m.Key] = load.then(l => {
                    const pageInfoOrError = l[m.PageName];
                    fn.resolveToError(pageInfoOrError, true);
                    return loadingClientInfo[m.Key].PreparedClient = pageInfoOrError;
                }));
                var result = $c.all(perPage).then(whenAllLoaded);
            } else {
                result = whenAllLoaded();
            }

            return result;
        });
    }
    /** already started resolve Page Client Info instances */
    private loadingClientInfo: Record<string, PromiseLike<INavPageClientInfo<any>> & { PreparedClient? : INavPageClientInfo<any> }> = {};

    /** trigger load of given Page Client informations */
    private loadPageClientInfos(pageNames: string[], navUrl: string, forceStatic?: boolean) {
        var queryParams = fn.EncodeQueryData({ navUrl: (navUrl || undefined), forceStatic: forceStatic });
        return this.$c.post<INamed<INavPageClientInfo_Data>>(
            "/infoma.Web.Nav/NavPageClient?" + queryParams, pageNames, { cache: true }).then(data => {
                return fn.Remap(data.data, (pageClientInfo, pageName) => {
                    try {
                        fn.resolveToError((pageClientInfo && pageClientInfo.MetaData) || pageClientInfo, true);
                        var pageClient = <INavPageClientInfo><any>new NavPageClientInfo(pageClientInfo, this, pageName, navUrl);
                        this.PrepareClient(pageClient.MetaData, pageClient);
                        return pageClient;
                    } catch (err) {
                        return err;
                    }
                });
            });
        }

    /** 'Enrich' MetaDataInformation loaded from Server with Client Util Props / Methods */
    public PrepareClient(mRoot: IPageMetaData, pageClientInfo?: INavPageClientInfo) {
        var me = this;
        // Column Retrieve Func
        function getColumnFunc(main: IPageMetaData, sub?: IPageMetaData) {
            return (p: IPageProperty, config?: { Prefix?: string, Editable?: boolean, SuppressHint?: boolean }) => {
                var prefix = (config && config.Prefix || "");
                var editable = (config && !!config.Editable);
                var col = kendoColumn(p, prefix, config && config.SuppressHint);
                if (editable && p && p.ReadOnly) {
                    // Caution : attributes.class might already exist from "kendoColum"!
                    if (col.attributes)
                        col.attributes.class = (col.attributes.class || "") + " idms_readonly";
                    else
                        col.attributes = { class: "idms_readonly" };
                }
                return (col &&
                    (col = me.customExtendColumn({ PageName: main.PageName, Property: p, Column: col, Prefix: prefix })))
                    || undefined;
            };

        }

        /** Resolve Name/Prop to Property of this Items MetaData */
        function ResolveProperty(this: IPageMetaData, prop: string | IPageProperty, validateProp?: boolean, useAlias?: boolean) {
            function validate(err: string) {
                if (validateProp === false)
                    return;
                if (validateProp === true || __ValidateProps)
                    throw new Error(err);
            }
            if (!prop) {
                validate("No Property/Name Specified");
                return;
            }
            if (typeof prop === "string")
                var propName = prop;
            else {
                let pathProp = (prop.Path && this.AllPropsByPath[prop.Path]);
                if (pathProp)
                    return pathProp;

                propName = prop.Name;
            }

            if (!(typeof (propName) == "string")) {
                validate("Invalid Parameter for resolveProperty");
                return;
            }
            if (propName == "") {
                validate("Invalid Parameter for resolveProperty: Emtpy");
                return;
            }
            var result = this.PropsByName[propName];

            if (!result && useAlias)
                result = this.Properties.filter(item => item.AliasName == propName)[0];

            if (!result && propName.indexOf(".") > 0) {

                // Check for full Path, e.g 'SalesLines.No'
                var check = propName.split(".");
                if (check.length == 2) {
                    if (!this.PropertyName) {
                        var rootProp = this.ResolveProperty(check[0], validateProp, useAlias);
                        if (rootProp && rootProp.ArrayProperties) {
                            result = rootProp.ArrayProperties.PropsByName[check[1]];
                        }
                    } else if (this.PropertyName == check[0]) {
                        result = this.PropsByName[check[1]];
                    }
                }
            }
            if (!result)
                validate("Property '" + propName + "' not found on '" + this.PageName + (this.PropertyName ? "." + this.PropertyName : "") + "'");
            return result;
        }

        // Attach Client Side Additional functionality:
        // - IPageClientInfo.AllProperties
        // - IPageClientInfo.AllMethods
        // - IPageMetaData.GetClientKey
        // - IPageProperty.Path

        var allProps = pageClientInfo && pageClientInfo.AllProperties;
        function ColumnIsInitialVisible(p: IPageProperty) {
            if (!p) {
                return false;
            }
            if (p.Invisible == true) {
                return true;
            }
            switch (p.GridVisibility) {
                case NavGridVisibility.ForcedRemove:
                case NavGridVisibility.InitialHidden:
                    return false;

                default:
                    return true;
            }
        }

        function extendProperty(p: IPageProperty, path: string) {
            p.Path = path;
            p.InitialVisible = ColumnIsInitialVisible(p);
            if (allProps) {
                allProps[path] = p;
            }
            if (p.EnumMappings) {
                p.EnumMappings.Empty = FindFirst(p.EnumMappings, p => p.IsEmpty == true) || FindFirst(p.EnumMappings, p => p.Enum == "_blank_" && p.IsEmpty !== false);
            }
        }

        var allProperties = new Array(...mRoot.Properties);
        mRoot.Properties.map(item => item.ArrayProperties).filter(fn.isSet).forEach(item => allProperties.push(...item.Properties));

        mRoot.GetClientKey = getClientKeyFunc(mRoot);
        mRoot.GetColumn = getColumnFunc(mRoot);
        mRoot.ResolveProperty = ResolveProperty;
        mRoot.Model = getOrCreateKendoModel(mRoot);
        mRoot.PropsByName = ToDictionary(mRoot.Properties, "Name");
        var model = getOrCreateKendoModel(mRoot);
        mRoot.Properties.forEach(mProp => extendProperty(mProp, mProp.Name));
        mRoot.Properties.filter(p => !!p.ArrayProperties).forEach(p => {
            var mSub = p.ArrayProperties,
                mModel = getOrCreateKendoModel(mSub);
            mSub.GetClientKey = getClientKeyFunc(p.ArrayProperties);
            mSub.Model = getOrCreateKendoModel(p.ArrayProperties);

            mSub.ResolveProperty = ResolveProperty;
            mSub.PropsByName = ToDictionary(mSub.Properties, "Name");
            mSub.Properties.forEach(mProp => { mProp.ParentProp = p; extendProperty(mProp, p.Name + "." + mProp.Name); });
            mSub.GetColumn = getColumnFunc(mRoot, mSub);
            mSub.AllPropsByPath = ToDictionary(mSub.Properties, "Path");
        });

        mRoot.AllPropsByPath = ToDictionary(allProperties, "Path");
        this.trigger("PrepareClient", { Page: mRoot, IsCustom: pageClientInfo && !pageClientInfo.FromStaticDescription });
    }

    /** Get (Cached) Page MetaData information */
    public GetPageMetaData<TPageName extends TDefinedPageName>(pageName: TPageName, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<IPageMetaData<TDefinedPageMaps[TPageName], TPageName>>;
    public GetPageMetaData<TPage = TDefaultPageObj>(pageName: string, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<IPageMetaData<TPage>>;
    public GetPageMetaData(pageName: string, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<IPageMetaData> {
        return this.GetPageClientInfo(pageName, navUrl, forceStatic).then(p => p.MetaData);
    }
    /** GetOr Load Page MetaData Information for Pages by PageName */
    public EnsurePageMetaData(pageNames: string[], navUrl: string): PromiseLike<Record<string, IPageMetaData>> {
        return this.GetPageClientInfos(<TDefinedPageName[]>pageNames, navUrl).then(r => fn.Remap(r, m => m.MetaData));
    }

    /** prepare Data from Server
    * - Typed DateTime Value
    * - Fillup Undefined Values
      */
    protected prepareData<TPage>(pageMetaData: IPageMetaData<TPage>, value: Partial<TPage>) {
        pageMetaData.Properties.forEach(item => {
            if (value[item.Name] === undefined) {
                switch (item.Type) {
                    case "String":
                        // NAV will omit not set strings, so explicitly set them
                        value[item.Name] = "";
                        break;

                    case "DateTime":
                        value[item.Name] = null;
                        break;

                    default:
                        // possible?
                        value[item.Name] = undefined;
                }
            }
            else
                switch (item.Type) {
                    case "DateTime":
                        value[item.Name] = fn.ToDate(value[item.Name]);

                        break;
                    case "String":
                        var strCheck = <string>value[item.Name];
                        if (strCheck == undefined)
                            value[item.Name] = "";
                        else if ((typeof strCheck === "string") && strCheck.indexOf("\\n") > 0) {
                            /* NAV 2009 uses "\" for NewLinMOe NAV 2013 "<cr><lf>"
                            /* NAV 2009 Webservice infoma uses "\n"
                            /* we translate "\n" -> to <cr><lf>"
                            /* we do not change the value back in the setter -> because it is saved in NAV 2013 mode */
                            var check = strCheck.replace(/(?:\\n)/g, '\r\n');
                            if (strCheck != check)
                                value[item.Name] = check;
                        }

                        break;
                    case "Boolean":
                    case "Decimal":
                    case "Int32":
                        break;
                    default:
                        if (item.ArrayProperties) {
                            // array
                            var arrayItems = <IPageObj[]>value[item.Name];

                            if (arrayItems && arrayItems.length)
                                arrayItems.forEach(arrItem => this.prepareData<any>(item.ArrayProperties, arrItem), this);

                        } else if (item.EnumMappings) {
                            // Enum
                        } else {
                            throw "Unhandled MetaData for '" + pageMetaData.PageName + "[" + pageMetaData.PropertyName + "]" + "', Type = '" + item.Type + "', Prop= '" + item.Name + "'";
                        }
                }
        }, this);

        return value;
    }

    /** Load NavPageItem instance by given Key Information (null if not found) */
    public LoadItem<TPage = TDefaultPageObj, TPageName extends string = string>(navKey: INavItemKey<TPage, TPageName>, forceStatic?: boolean) {
        var loadInfo = this.GetPageClientInfo<TPage>(navKey.PageName, navKey.NavUrl, forceStatic),
            loadData = this.Read<TPage>(navKey, forceStatic).then(d => d.Data);
        return this.ConstructItem<TPage>(loadData, loadInfo, navKey.InstanceID);
    }

    public  ManaulConstructItem<TPage = TDefaultPageObj, TPageName extends string = string>(navKey: INavItemKey<TPage, TPageName>, loadData : TPage | PromiseLike<TPage>, forceStatic?: boolean) {
      var loadInfo = this.GetPageClientInfo<TPage>(navKey.PageName, navKey.NavUrl, forceStatic);
      return this.ConstructItem<TPage>(loadData, loadInfo, navKey.InstanceID);
  }

    /** Construct PageItem form necessary data/Promisedata */
    public ConstructItem<TPage extends IPageObj = TDefaultPageObj>(rawData: PromiseLike<TPage> | TPage, pageInfo: PromiseLike<INavPageClientInfo<TPage>> | INavPageClientInfo<TPage>, wfInstanceId: string) {
        return this.constructRead<TPage>(rawData, pageInfo).then(rawData => {
            if (!rawData.Data)
                return null;
            return this.createItem<TPage>(rawData.Client, { wfInstanceId: wfInstanceId, rawServerData: rawData.Data, prepared: true });
        });
    }

    /** construct Data from RawData and MetaData Informatation */
    protected constructRead<TPageObj extends IPageObj>(rawData: PromiseLike<TPageObj> | TPageObj, pageClientInfo: INavPageClientInfo<TPageObj> | PromiseLike<INavPageClientInfo<TPageObj>>) {
        var $c = this.$c;
        return $c.when(pageClientInfo).then(pageClientInfo => {
            return $c.when(rawData).then(rawData => {
                return <INavReadResult<TPageObj>>{
                    Data: (rawData && this.prepareData(pageClientInfo.MetaData, rawData)) || null,
                    Client: pageClientInfo
                };
            });
        });
    }

    /** Craete New Item (Client Side Only, Update will implicitly try to create in NAV */
    public CreateNew<TPage = TDefaultPageObj>(client: INavPageClientInfo<TPage>, values: Partial<TPage>, wfInstanceId?: string) {
        var result = this.createItem<TPage>(client, { rawServerData: values, wfInstanceId: wfInstanceId, isCreateNew: true });
        return result && (result.IsNew = true) && result;
    }

    /** Delete Instance in Nav */
    public Delete(pageItem: NavPageItem, noEnqueue?: boolean) {
        var optEnqueue = noEnqueue ? ((a: () => PromiseLike<any>) => a()) : (a: () => PromiseLike<any>) => pageItem.Anchor.EnqueueOperation(a);

        return optEnqueue(() => {
            if (pageItem.IsDeletedInNav)
                throw new Error("Element bereits gelöscht!");
            if (pageItem.IsNew === true)
                throw new Error("Ein neues Element kann nicht gelöscht werden!");

            var stateInfo = this.getCurrentStateInfo(pageItem);

            return this.$c.post<{ Items: any[] }>("/infoma.Web.Nav/NavPageClient/DeleteItem", stateInfo.ClientData).then(data => {
                var reloaded = data.data.Items.map(item => item && this.createItem(stateInfo.Client, {
                    isCreateNew: false,
                    prepared: false,
                    rawServerData: item,
                    wfInstanceId: stateInfo.Anchor.InstanceID,
                }));
                stateInfo.OperateItems.Sync(reloaded);
            });
        });
    }

    /** Load Multiple Page Items by via ReadMultiple, TODO : InstanceId for UIConfirm Handling */
    public LoadPageItems<TPageName extends TDefinedPageName>(queryParams: IPageQueryParameters<TDefinedPageMaps[TPageName], TPageName>, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<NavPageItems<TDefinedPageMaps[TPageName]>>;
    public LoadPageItems<TPage extends IPageObj = TDefaultPageObj>(queryParams: IPageQueryParameters<TPage>, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<NavPageItems<TPage>>;
    public LoadPageItems<TPage>(queryParams: IPageQueryParameters<TPage>, navUrl: TNavUrlAccess, forceStatic?: boolean): PromiseLike<NavPageItems<TPage>> {
        let readPageMeta = this.GetPageMetaData(queryParams.PageName, navUrl, forceStatic),
            readData = this.ReadMultiple<TPage>(queryParams, navUrl, forceStatic);
        return readPageMeta.then(pageMeta => {
            return readData.then(readInfo => {
                var allItems = readInfo.Data;
                allItems.forEach(value => this.prepareData(pageMeta, value));

                var result = <NavPageItems<TPage>>new NavData.NavPageItems(this, readInfo.Client, allItems, queryParams.Constraints, readInfo.Bookmark.AllRead);
                result.Anchor.InstanceID = queryParams.InstanceID;
                return result;
            });
        });
    }

    /** Load Multiple Page Items by via ReadMultiple, TODO : InstanceId for UIConfirm Handling */
    public LoadItemsSeperate<TPage = TDefaultPageObj>(queryParams: IPageQueryParameters<TPage>, navUrl: TNavUrlAccess, forceStatic?: boolean) {
        return this.GetPageClientInfo<TPage>(queryParams.PageName, navUrl, forceStatic).then(pageClientInfo => {
            return this.ReadMultiple<TPage>(queryParams, navUrl, forceStatic).then(readData => {
                return readData.Data.map(data => this.createItem<TPage>(pageClientInfo, { rawServerData: data, prepared: true }));
            });
        });
    }

    /** Read Item By Key, result is "Raw Data" from corespoding NavClient's "Read" method (null if element not found!)s */
    public Read<TPageName extends TDefinedPageName>(navKey: INavKey<TDefinedPageMaps[TPageName], TPageName>, forceStatic?: boolean): PromiseLike<INavReadResult<TDefinedPageMaps[TPageName]>>;
    public Read<TPageObj extends IPageObj>(navKey: INavKey<TPageObj>, forceStatic?: boolean): PromiseLike<INavReadResult<TPageObj>>;
    public Read<TPageObj extends IPageObj>(navKey: INavKey<TPageObj>, forceStatic?: boolean): PromiseLike<INavReadResult<TPageObj>> {
        var loadMetaData = this.GetPageClientInfo<TPageObj>(navKey.PageName, navKey.NavUrl, forceStatic),
            loadRawData = this.$c.post<TPageObj>("/infoma.Web.Nav/NavPageClient/ReadItem", navKey).then(data => data.data);
        return this.constructRead<TPageObj>(loadRawData, loadMetaData);
    }

  public GetRecIdFromKey(url: TNavUrlAccess, pageName: string, key: string) {
    return this.ResolveUrl(url, true).then(navUrlInfo => {
      var params = fn.EncodeQueryData({
        pageName: pageName,
        navUrl: navUrlInfo.FullUrl
      });
      return this.$c.post<{ RecId: string }>("/infoma.Web.Nav/NavPageClient/GetRecIdFromKey?" + params, { Key: key }).then(d => {
        return d.data.RecId;
      });
    });
  }
    /** Get All Companies Infos per Nav Url */
    //public GetCompanyInfos(navUrl: string, forceRefresh?: boolean) {
    //    var result = (!forceRefresh) && this._getCompanyInfos[navUrl];
    //    if (!result)
    //        result = this._getCompanyInfos[navUrl] = this.$c.get<(string | ICompanyInfo)[]>("/infoma.Web.Nav/NavPageClient/GetCompanyInfos?" +
    //            fn.EncodeQueryData({ navUrl: navUrl })).then(result => {
    //                var infos = result.data.map(item => {
    //                    if (typeof (item) == "string")
    //                        return <ICompanyInfo>{
    //                            Name: item
    //                        }
    //                    else
    //                        return item;
    //                });
    //                return infos;
    //            });
    //    return result;
    //}
    //private _getCompanyInfos: INamed<PromiseLike<ICompanyInfo[]>> = {};

    /** Validate Company, only throwing Error if Companies can succesfully be resolved, but given company is not part of accessible Companies */
    public ValidateCompany(navUrl: TNavUrlAccess, company: string, shortMessage?: boolean) {
        return this.GetCompanies(navUrl).then(companies => {
            if (company && companies.indexOf(company) < 0) {
                let msg = "Mandant '" + company + "' nicht verfügbar";
                if (shortMessage == false)
                    msg += "\n\nVerfügbare Mandanten:\n" + companies.join(", ");
                throw new Error(msg);
            }
            return company;
        }, err => { });
    }

    /** Get Configured NAV Accesses from Server Config */
    public GetAvailableUrls(forceRefresh?: boolean) {
        var result = (!forceRefresh) && this._getAvailableUrls;
        if (!result)
            result = this._getAvailableUrls = this.$c.get<INavUrlInfoEx[]>("/infoma.Web.Nav/GetAvailableUrls").then(data => data.data);
        return result;
    }
    private _getAvailableUrls: PromiseLike<INavUrlInfoEx[]>;

    /** Create Instance from Server Data */
    private createItem<TPage extends IPageObj>(pageClientInfo: INavPageClientInfo<TPage>, config: {
        wfInstanceId?: string,
        rawServerData: Partial<TPage>,
        prepared?: boolean,
        isCreateNew?: boolean,
        anchor?: NavItemAnchor
    }) {
        var rawServerData = config.rawServerData;
        pageClientInfo.MetaData.Properties.filter(item => !!item.ArrayProperties).forEach(arrProp => {
            var rawItems = <any[]>rawServerData[arrProp.Name] || [];
            var lineModelType = getOrCreateKendoModel(arrProp.ArrayProperties);
            rawServerData[arrProp.Name] = new ObservableArray(rawItems.map(rawItem => new lineModelType(rawItem)));
        }, this);

        if (config.isCreateNew && !rawServerData.Key) {
            rawServerData.Key = "_newItem"; // Special Marker, evaluates to "Create" instead of "Update" in ServerFunctionality!
        }
        if (!rawServerData.Key) {
            throw new Error("NavClientService.createItem : Unexpected data");
        }

        if (!config.prepared)
            this.prepareData<TPage>(pageClientInfo.MetaData, rawServerData);

        var model = <Model & TPage>new (getOrCreateKendoModel(pageClientInfo.MetaData))(rawServerData);
        // Build "BackupComparison" for Changed Values from prepared Data
        var result: NavPageItem<TPage> = new NavData.NavPageItem(pageClientInfo, this, model, fn.clone(rawServerData), config.anchor);
        if (config.wfInstanceId)
            result.Anchor.InstanceID = config.wfInstanceId;
        return result;
    }

    /** Execute Method on Item */
    public ExecuteMethod(pageItem: NavPageItem | NavPageItems, methodConfig: INavMethodConfig) {
        return this.executeUpdate({ Items: pageItem, MethodConfig: methodConfig });
    }

    /** Get Information about Method on Client */
    public GetMethodInfo(mInfo: { methodName: string; pageName: string, navUrl: string }) {
        var params = fn.EncodeQueryData(<any>mInfo);
        return this.$c.get<{ Exists: boolean, Parameters: string[] }>("/infoma.Web.Nav/NavPageClient/GetMethodInfo?" + params, { cache: true })
            .then((data) => data.data);
    }

    /**
     * Determine Information for Sending PageItem(s) Information to Server
     * @param pageItems
     * @param forceFullInfo : Send full Current Info to Server, not only Pending Changes
     */
    private getCurrentStateInfo(pageItems: NavPageItem | NavPageItems, forceFullInfo?: boolean) {
        var operateItems = pageItems,
            anchor = pageItems.Anchor,
            obs = IsNavPageItem(pageItems) ? pageItems.Model : pageItems;

        if (IsNavPageItems(pageItems)) {
            var currItems = pageItems.Items,
                readMultipleConstraints = pageItems.Params,
                readMultilepleCurrentIdx = pageItems.Current ? currItems.indexOf(pageItems.Current) : (pageItems.FirstSelected >= 0 ? pageItems.FirstSelected : undefined);

        } else if (IsNavPageItem(pageItems)) {
            if (pageItems.Container) {
                // In Case of an Container, execute via
                operateItems = pageItems.Container;
                var currItems = operateItems.Items;
                readMultilepleCurrentIdx = currItems.indexOf(pageItems);
                readMultipleConstraints = operateItems.Params;
            } else {
                var currItems = [pageItems];
            }
        } else {
            fn.never(pageItems, "Invalid Items object for NAV Operation");
        }

        if (forceFullInfo)
            var serverItems = currItems.map(pageItem => <any>pageItem.Model); // Row Properties might need other Values to "Function" correctly....
        else
            var serverItems = currItems.map(pageItem => pageItem.GetCurrentServerChanges());

        var dataToPersist = serverItems.map(serverData => NavPageItem.getServerData(pageItems.ClientInfo.MetaData, serverData));
        return {
            ClientData: {
                Items: dataToPersist,
                Filters: readMultipleConstraints,
                CurrentIdx: readMultilepleCurrentIdx,
                PageName: pageItems.ClientInfo.MetaData.PageName,
                NavUrl: pageItems.ClientInfo.Url,
                InstanceID: anchor.InstanceID,
            },
            OperateItems: operateItems,
            Client: pageItems.ClientInfo,
            Anchor: anchor
        };
    }

    /** Execute Update Operation, returns same instance after resync,  */
    executeUpdate(update: { Items: NavPageItems | NavPageItem, MethodConfig?: INavMethodConfig, UIItem?: IDMSUILine }):
        PromiseLike<{ Items: NavPageItem | NavPageItems, MethodResult: { [name: string]: any } }> {
        const pageItems = update && update.Items;
        const $c = this.$c;

        if (!(IsNavPageItem(pageItems) || IsNavPageItems(pageItems)))
            return $c.reject("Expected NavageItem(s) object");

        var obs = IsNavPageItem(pageItems) ? pageItems.Model : pageItems,
            methodConfig = update.MethodConfig,
            /** RowProperty might need full Information on server ... */
            transferFullInfoToServer = methodConfig && !!methodConfig.RowProperty,
            stateInfo = this.getCurrentStateInfo(update.Items, transferFullInfoToServer);

        // allow externally to prevent server Communication
        if (obs.trigger($PageItemExecuteUpate))
            return $c.when({ Items: pageItems, MethodResult: {} });

        const triggerUpdate = <T>(t: T) => {
            obs.trigger($PageItemExecutedUpdate, { MethodConfig: update.MethodConfig });
            return t;
        };
        return $c.finally($c.post<IPageItemUpdateResult>("/infoma.Web.Nav/NavPageClient/UpdateItem", {
            ClientData: stateInfo.ClientData,
            UIConfirmItem: update.UIItem,
            Method: methodConfig
        }).then(data => data.data).then(data => {
            var UIConfirmItem = data.UIConfirmItem,
                refreshItems = data.Items;
            if (UIConfirmItem) {
                if (refreshItems)
                    stateInfo.OperateItems.SyncKeys(refreshItems);
                stateInfo.Anchor.FireConfirm(methodConfig, UIConfirmItem);

                return this.$navui.UIAnswerConfirmMessage(UIConfirmItem).then(() =>
                    this.executeUpdate({ Items: pageItems, MethodConfig: methodConfig, UIItem: UIConfirmItem }));
            } else {
                var reloaded = refreshItems.map(item => item && this.createItem(pageItems.ClientInfo, {
                    isCreateNew: false,
                    prepared: false,
                    rawServerData: item,
                    wfInstanceId: pageItems.Anchor.InstanceID,
                }));

                stateInfo.OperateItems.Sync(reloaded);

                if (data.IsCancel) {
                    fn.error.ThrowCancel();
                }
                return { Items: pageItems, MethodResult: data.MethodResult };
            }
        }), /** finally*/ triggerUpdate);
    }

    /** Insert New Row Item */
    public InsertNewRowItem(pageItem: NavPageItem, propertyName: string) {
        var me = this;
        var prop = pageItem.ClientInfo.AllProperties[propertyName];
        var customInfo = prop && prop.ArrayProperties && prop.ArrayProperties.CustomInfo;
        var insertMethName = customInfo && customInfo.InsertRowMethod;
        if (!insertMethName)
            return this.$c.reject("Methode 'InsertRowMethod' nicht definiert");

        return this.executeUpdate({ Items: pageItem, MethodConfig: { MethodName: insertMethName } }).then(item => this);
    }

    /** Delete Row Item*/
    public DeleteRowItem(row: NavPropertyItem) {
        var pageItem = row.Root;
        var deleteRowMethod = row.MetaData.CustomInfo && row.MetaData.CustomInfo.DeleteRowMethod;
        if (!deleteRowMethod)
            return this.$c.reject("Methode 'DeleteRowMethod' nicht definiert");

        return this.executeUpdate({
            Items: pageItem, MethodConfig: {
                MethodName: deleteRowMethod,
                RowProperty: row.MetaData.PropertyName,
                RowKey: row.Model["Key"]
            }
        }).then(item => this);
    }

    /** Duplicate Row Item, returns a NEW reloaded item */
    public DuplicateRowItem(row: NavPropertyItem): PromiseLike<NavPageItem> {
        var pageItem = row.Root,
            rowMeta = row.MetaData,
            duplicateMethod = rowMeta.CustomInfo && rowMeta.CustomInfo.DuplicateRowMethod;
        if (!duplicateMethod)
            return this.$c.reject("Methode 'DuplicateRowMethod' nicht definiert");

        return this.executeUpdate({
            Items: pageItem, MethodConfig: {
                MethodName: duplicateMethod,
                RowProperty: row.MetaData.PropertyName,
                RowKey: row.Model["Key"]
            }
        }).then(item => <NavPageItem>item.Items);
    }

    /** Get View Configuration for PageName and given View Name */
    public GetView(pageInfo: INavPageClientInfo, viewName?: string) {
        var queryParams = fn.EncodeQueryData({ pageName: pageInfo.MetaData.PageName, viewName: viewName, navUrl: pageInfo.Url });
        return this.$c.get<IViewPageDisplay>("/infoma.Web.Nav/NavPageClient/GetViewConfiguration?" + queryParams, { cache: true })
            .then(data => data.data);
    }

    /** Get Lookup Information for a Property  */
    public GetLookupInfo(pageItem: NavPropertyItem, prop: IPageProperty) {
        const root = pageItem.Root,
            clientInfo = root.ClientInfo,
            $c = this.$c;

        // No server Callback necessary if Lookup is static, static Lookups already attached to Property
        if (prop.HasLookup && prop.StaticLookup) {
            return $c.when(prop.StaticLookup);
        }

        var determineLookup: IDetermineDynamicLookup = {
            NavUrl: clientInfo.Url,
            PageName: clientInfo.MetaData.PageName,
            CurrentItem: root.Model,
            LineProperty: pageItem.MetaData.PropertyName,
            LineKey: pageItem.MetaData.PropertyName ? pageItem.Model["Key"] : "",
            PropertyName: prop.Name
        };

        return $c.post<ILookupInfo>("/infoma.Web.Nav/NavPageClient/GetLookupInfo", determineLookup).then(data => {
            const eventData: IDetermineDynamicLookupEvent = { LookupInfo : data.data, DetermineLookup : determineLookup };
            this.trigger("AfterGetLookupInfo", eventData);
            return eventData.LookupInfo;
        });
    }

    /** Load Static Lookup Definition if possible*/
    public GetStaticLookupInfo(navUrl: string, pageName: string, property: string) {
        return this.$c.post<ILookupInfo>("/infoma.Web.Nav/NavPageClient/GetStaticLookupInfo?" + fn.EncodeQueryData({
            navUrl: navUrl,
            pageName: pageName,
            property: property
        }), {}).then(d => d.data);
    }

    /** Perform ReadMultiple on Page
    * WOut : query Params : first PageBlock
    * for paging : set Parameters from previous result
    */
    public ReadMultiple<TPageName extends TDefinedPageName>(pageQueryParams: IPageQueryParameters<TDefinedPageMaps[TPageName], TPageName>, nav?: TNavUrlAccess, forceStatic?: boolean, config?: IRequestShortcutConfig): PromiseLike<IPageQueryResult<TDefinedPageMaps[TPageName], TPageName>>;
    public ReadMultiple<TPage extends IPageObj = TDefaultPageObj>(pageQueryParams: IPageQueryParameters<TPage>, nav?: TNavUrlAccess, forceStatic?: boolean, config?: IRequestShortcutConfig): PromiseLike<IPageQueryResult<TPage>>;
    public ReadMultiple<TPage = any>(pageQueryParams: IPageQueryParameters, nav?: TNavUrlAccess, forceStatic?: boolean, config?: IRequestShortcutConfig): PromiseLike<IPageQueryResult> {
        return this.GetPageClientInfo<TPage>(pageQueryParams.PageName, nav, forceStatic).then(client => {
            let queryParams = fn.EncodeQueryData({ navUrl: client.Url }),
                metaData = client.MetaData;

            return client.ResolveSearchOptimization(pageQueryParams).then(pageQueryParams => {
                if (pageQueryParams.NoResult == true) {
                    var dummyResult: IPageQueryResult<TPage> = {
                        Data: [],
                        Bookmark: { AllRead: true, TotalReadCount: 0, PageCount: 0, BOF: true, CurrPageIndex: 0, EOF: true, Back: false },
                        Client: client,
                    };
                    return dummyResult;
                }

                return this.$c.post<IPageQueryResult<TPage>>("/infoma.Web.Nav/NavPageData?" + queryParams, this.normalizeParameters(pageQueryParams, client), config).then(c => {
                    var result = <IPageQueryResult<TPage>>c.data;
                    resolveToError(result, true);

                    result.Data = fixupData(metaData.Properties, result.Data);
                    result.Client = client;
                    this.trigger("ReadMultiple_" + metaData.PageName, { Data: result });
                    return result;
                });
            });
        });
    }

    /** Register Custom ReadMultiple Interception Callback */
    RegisterReadMultiple(info: { PageName: string }, callback: (data: IPageQueryResult<IPageObj>) => void) {
        this.bind("ReadMultiple_" + info.PageName, (e) => callback(e.Data));
    }

    /** Register Custom Column Handling Callback for a Property  */
    RegisterCustomColumn(info: { PageName?: string; Property: string },
        adjustColumnCallBack: (opt: { Column: GridColumn; Property: IPageProperty; Prefix: string; }) => void) {
        var id = (info.PageName || "*") + "." + info.Property;
        this.bind("CustomColumn_" + id, (e) => {
            adjustColumnCallBack(e.Info);
        });
    }

    /** Register Custom Event on Parparing Loaded Client Info */
    RegisterPrepareClient(callBack: (e: { Page: IPageMetaData, IsCustom: boolean }) => any) {
        this.bind("PrepareClient", callBack);

        // Trigger for already loaded Items
        var alreadyLoaded = fn.DictToArray(this.loadingClientInfo).map(item => item.PreparedClient).filter(fn.isSet);
        alreadyLoaded.forEach(item => callBack({ Page : item.MetaData, IsCustom: !item.FromStaticDescription }));
    }

    /** Register Value Transform  */
    RegisterValueTransform(info: { PageName?: string; Property: string }, config: {
        CustomColumn?: (opt: { Template: string; readonly Column: GridColumn; readonly Property: IPageProperty; readonly Prefix: string }) => void,
        /** TODO Unify Column Generation for DropDowns */
        CustomDDColumn?: (opt: { Template: string, readonly Property: IPageProperty; readonly Prefix: string; }) => void,
        CustomConstraint?: (e: { Constraint: IPageQueryConstraint }) => void,
        PrepareClient: (e: { readonly OnItem: NavPropertyItem, readonly Prop: IPageProperty, value: any }) => void,
        RevertServer: (e: { readonly OnItem: NavPropertyItem, readonly Prop: IPageProperty, value: any }) => void,
    }) {
        var id = (info.PageName || "*") + "." + info.Property;
        this.bind("CustomPrepareClient_" + id, e => config.PrepareClient(e));
        this.bind("CustomPrepareServer_" + id, (e) => config.RevertServer(e));
        if (config.CustomColumn)
            this.RegisterCustomColumn(info, config.CustomColumn);

        const CustomDDColumn = config.CustomDDColumn;
        if (CustomDDColumn) {
            this.bind("CustomDDCol_" + id, e => CustomDDColumn(e));
        }
        const CustomConstraint = config.CustomConstraint;
        if (CustomConstraint) {
            this.bind("CustomConstraint_" + id, e => CustomConstraint(e));
        }
    }

    /** Trigger Custom Column Handling Callbacks */
    private customExtendColumn(info: { PageName: string; Property: IPageProperty; Prefix: string; Column: GridColumn }) {
        var id = info.PageName + "." + info.Property.Path;
        var idS = "*." + info.Property.Path;
        this.trigger("CustomColumn_" + id, { Info: info });
        this.trigger("CustomColumn_" + idS, { Info: info });
        return info.Column;
    }

    /** Transform Value for Server Representation (see RegisterValueTransform) */
    public PrepareServerValue(item: NavPropertyItem, prop: IPageProperty, value: any) {
        var id = item.MetaData.PageName + "." + prop.Path;
        var idS = "*." + prop.Path;
        var e = { OnItem: item, Prop: prop, value: value };
        this.trigger("CustomPrepareServer_" + id, e);
        this.trigger("CustomPrepareServer_" + idS, e);
        return e.value;
    }

    /** Transform Value for Client Representation (see RegisterValueTransform) */
    public PrepareClientValue(item: NavPropertyItem, prop: IPageProperty, value: any) {
        var id = item.MetaData.PageName + "." + prop.Path;
        var idS = "*." + prop.Path;
        var e = { OnItem: item, Prop: prop, value: value };
        this.trigger("CustomPrepareClient_" + id, e);
        this.trigger("CustomPrepareClient_" + idS, e);
        return e.value;
    }

    public PrepareDDColumn(p: IPageMetaData, prop: IPageProperty, prefix: string, template: string) {
        var id = p.PageName + "." + prop.Path;
        var idS = "*." + prop.Path;
        const was = template;
        var e = { Template: template, Property: prop, Prefix: prefix };
        this.trigger("CustomDDCol_" + id, e);
        this.trigger("CustomDDCol_" + idS, e);
        return e.Template;
    }

    public PrepareConstraint(q: IPageQueryConstraint, pageName: string) {
        var id = pageName + "." + q.FieldName;
        var idS = "*." + q.FieldName;
        const e = { Constraint: q };
        this.trigger("CustomConstraint_" + id, e);
        this.trigger("CustomConstraint_" + idS, e);
    }

    private normalizeParameters(params: IPageQueryParameters, client: INavPageClientInfo) {
        if (!client.CustomSource && params && params.Constraints && params.Constraints.forEach) {
            const metaData = client.MetaData;
            params.Constraints.forEach(item => {
                var field = metaData.PropsByName[item.FieldName];
                // Decimal : Force Eval as Expression if number like ('10,99' will be not recognized otherwise (but '=10,99' will)
                if (field && (field.Type == "Decimal") && ("" + item.Value).trim().match(/^[0-9\,\.]+$/)) {
                    item.Value = "(" + item.Value + ")";
                }
                if (item.Value != null) {
                    item.Value = "" + item.Value; // Problem right now : Visualization gives Array for enum Typed properties: enforce as String
                }

                if (item.Op == "eq") {
                    item.Value = "=" + item.Value;
                    delete item.Op;
                }
            });
        }
        return params;
    }

    public GetEncryptionInfo() {
        return this.$c.get<IEncryptionInfo>("/infoma.Web.Nav/Utils/EncryptionInfo", {})
            .then((r) => {
                resolveToError(r.data);
                return r.data;
            });
    }

    /** Encrypt string Value */
    public Encrpyt(value: string, asBinary: boolean) {
        return this.GetEncryptionInfo().
            then((info) => {
                var key = new RSAKeyPair(info.Info.Exponent, "", info.Info.Modulus);

                if (asBinary) {
                    var charCodes = [];
                    for (var i = 0; i < value.length; i++)
                        charCodes.push(value.charCodeAt(i));

                    value = charCodes.join(",");
                }
                return key.encrypt(info.Challenge + value);
            });
    }

    /** Validate User Password */
    public ValidateUserPwd(pwd: string, mock?: boolean) {
        var outerResult = this.$c.defer<any>();
        try {
            var resolve = this.GetEncryptionInfo().
                then((info) => {
                    var key = new RSAKeyPair(info.Info.Exponent, "", info.Info.Modulus);
                    var val = info.Challenge + pwd;
                    var charCodes = [];
                    for (var i = 0; i < pwd.length; i++)
                        charCodes.push(pwd.charCodeAt(i));

                    var encrypted = key.encrypt(info.Challenge + pwd);
                    var encryptedCharCodeData = key.encrypt(info.Challenge + charCodes.join(","));

                    return this.$c.post<IPwdData>("/infoma.Web.Nav/Utils/ValidateUser", <IPwdData>{
                        EncryptedData: encrypted,
                        EncryptedCharCodeData: encryptedCharCodeData,
                        ErrorMessage: "",
                        Validated: undefined,
                        Mock: !!mock
                    }).then((result) => {
                        resolveToError(result.data, true);
                        if (result.data.Validated !== true)
                            throw new Error(result.data.ErrorMessage);
                    });
                });

            resolve.then(s => outerResult.resolve(undefined), err => outerResult.reject(err));
        } catch (err) {
            outerResult.reject(err);
        }

        return outerResult.promise;
    }

    /** Get Formula Evaluating on Property Item by text of formula (preventing write access) */
    public GetEvalFormula(formula: string) {
        try {
            var func = this.$c.parse(formula);
        } catch (err) {
            throw new Error("Formel: '" + formula + "':\n" + err);
        }

        return (item) => {
            try {
                return func(item.AsReadonly());
            } catch (err) {
                throw new Error("Fehler bei der Auswertung von '" + formula + "':\n" + err);
            }
        };
    }
}

/** Possible Definition for a Custom WebApi Root */
export interface IWebApiRoot {
    /** The Custom Web Api Root without trainling backslash! */
    WebApiRoot: string;

    /** Custom 'WithCredentials Setting' */
    WithCredentials?: boolean;
}

/** Transform a Page Property in an kendo Grid Column Representation */
export function kendoColumn(p: IPageProperty, preFix?: string, suppressHint?: boolean): GridColumn {
    var propName = (preFix ? preFix : "") + p.Name;
    var propTitle = p.Title;
    // With Explicit Prefix , access Value via calculated getter function [see kendo.getter], otherwise directly by name
    var strGetter = (preFix ? "kendo.getter('" + propName + "', true)(data)" : "data." + p.Name);
    var titleHint = suppressHint ? '' : "title='#: " + strGetter + " ? " + strGetter + " : '' " + " #'";

    var result = <GridColumn>{ field: propName, title: htmlEncode(propTitle), width: '160px' };
    switch (p.Type) {
        case "Boolean":
            result.template = "#: " + strGetter + "  ? 'Ja' : 'Nein' #";
            result.groupHeaderTemplate = "#: '" + propTitle + "' + ': ' + (data.value === true ? 'Ja' : (data.value === false ? 'Nein' : data.value )) #";
            result.filterable = { messages: { isTrue: "Ja", isFalse: "Nein" } };
            break;
        case "Decimal":
            result.format = NumericFormat[p.DecimalNumericType || 0];
            result.attributes = { "class": "idms_CurrencyField" }; // Right all align Numeric Fields
            break;
        case "Int32":
            result.filterable = {
                cell: { // Force no decimals on filter input
                    template: (c: { element: JQuery }) => {
                        if (c && c.element) {
                            c.element.kendoNumericTextBox({ format: "#", decimals: 0 });
                        }
                    }
                }
            };
            break;
        case "DateTime":
            //result.template = "#: " + p.Name  + " ?   kendo.toString(" + p.Name + ",'" + infoma.constants.DateFormat + "') : ''#";
            var dtFormat = DateFormat[p.DateFormat || 0];
            result.format = dtFormat;
	    // With adjusted DateTimeGrouping we can ignore Time in grouping caption
            const headerFormat = DateTimeGroupingAdjusted ? constants.KendoDateFormat : dtFormat;
            result.groupHeaderTemplate = `#:'${propTitle}' + ': ' + (data.value ? (kendo.format('${headerFormat}', data.value)) : '') #`;
            result.filterable = {
                cell: { // Force no decimals on filter input
                    template: (c: { element: JQuery }) => {
                        if (c && c.element) {
                            const picker = new InfDatePicker(c.element, {});
                        }
                    }
                }
            };
            //alert("Format von " + p.Name + ": " + result.format);
            break;
        case "String":
            result.template = ktemplate("<div class='idms_truncate' " + titleHint + ">#: " + strGetter + " ? " + strGetter + " : '' " + " #</div>");
            // Perf Opt : pre Select "Contains", not changeable by user
            result.filterable = {
                cell: { showOperators: false, operator: "contains" }
            };
            break;

        default:
            var mappings = p.EnumMappings;
            if (mappings && !mappings._TemplateIgnore && mappings.length > 0) {
                result.values = mappings.map(m => { return { text: m.Title, value: m.Value }; });
            } else {
                result.template = ktemplate("<div class='idms_truncate' " + titleHint + ">#: " + strGetter + " ? " + strGetter + " : '' " + " #</div>");
                // better : e.g. http://www.telerik.com/forums/grid-fixed-row-height-%28-no-word-wrap-no-multi-line-%29
            }
    }

    switch (p.GridVisibility) {
        case NavGridVisibility.InitialHidden:
            result.hidden = true;
            break;
        case NavGridVisibility.InitialVisible:
            result.hidden = false;
            break;
        default:
        // nothing to do
    }
    return result;
}

/** Utility to explicitly Validate User Password */
interface IPwdData {
    EncryptedData: string;

    Validated: boolean;

    ErrorMessage: string;
}

/** Result Information for a "Read" Operation */
export interface INavReadResult<TPageObj extends IPageObj> {
    /** Typed Read Item, or null if no data Read */
    Data: TPageObj;

    /** Used Client Information */
    Client: INavPageClientInfo<TPageObj>;
}

/** Result of an Update or Method Call */
export interface IPageItemUpdateResult {
    /** Confirm Item to be handled by User : Operation failed, Answer Message and try again; */
    UIConfirmItem?: IDMSUILine;
    /** "User Cancellation Error after UIConfirm. i.e. silent continuation" */
    IsCancel: boolean;
    /** The Item Data, will not be set if
     * - UIConfirmItem is set
     * - Item Destroyed (might happen in method call)
     */
    Items: IPageObj[];
    /** For Method call : Result (so far "Result" : Method Call Result, TODO : out Parameters) */
    MethodResult: { [name: string]: any };
}

interface IEncryptionInfo {
    Challenge: string;
    Info: {
        Exponent: string;
        Modulus: string;
        UserInfo: { DisplayName: string; Username: string }
    };
}

/** Company Related Info */
interface ICompanyInfo {
    /** Company Name */
    Name: string;
    /** Name of the FormsServer Customization Config */
    Config?: string;
}

/** Fix some Issues with Query data from server before handling on client:
       * 1.) Ensure even not transferred Props have explicit default value : problems with bind otherwise
       * 2.) DateTime Values get transferred as string: type them;
       */
export function fixupData(props: IPageProperty[], data: any[], model?: typeof Model): any[] {
    if (props && data && data.length > 0) {
        props.forEach(p => {
            let pType = p.Type,
                pName = p.Name;
                switch(pType) {
                    case "Boolean":
                        data.forEach(item => item[pName] = !!item[pName]);
                        break;
                    case "DateTime":
                        data.forEach(item => item[pName] = fn.ToDate(item[pName]));
                        break;
                    default:
                        data.forEach(item => {
                            if (item[pName] === undefined) {
                                switch (pType) {
                                    case "String":
                                        item[pName] = "";
                                        break;
                                    default:
                                        item[pName] = undefined;
                                }
                            } else if (pType === "String") {
                                var oldValue = <string>item[pName] || "";
                                if (oldValue != "" && oldValue.indexOf("\\n") >= 0) {
                                    /* NAV 2009 uses "\" for NewLinMOe NAV 2013 "<cr><lf>"
                                    /* NAV 2009 Webservice infoma uses "\n"
                                    /* we translate "\n" -> to <cr><lf>"
                                    /* we do not change the value back in the setter -> because it is saved in NAV 2013 mode */
                                    var newValue = ("" || item[pName]).replace(/(?:\\n)/g, '\r\n');
                                    if (newValue != oldValue) {
                                        item[pName] = newValue;
                                    }
                                }
                            }
                        });
                }
        });
    }

    if (model)
        data = data.map(item => new model(item));
    return data;
}
